import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
	type CreateFlashSaleInput,
	type FlashSalesFilter,
	type UpdateFlashSaleInput,
	flashSalesApi,
} from "./flash-sales-api";

export const KEY_FLASH_SALES = ["flash-sales"];

export function useFlashSalesList(
	filter: FlashSalesFilter = {},
	enabled = true,
) {
	return useQuery({
		queryKey: [...KEY_FLASH_SALES, "list", filter],
		queryFn: () => flashSalesApi.list(filter),
		enabled,
	});
}

export function useFlashSaleKpis() {
	return useQuery({
		queryKey: [...KEY_FLASH_SALES, "kpis"],
		queryFn: () => flashSalesApi.getKpis(),
	});
}

export function useFlashSale(id: string | undefined) {
	return useQuery({
		queryKey: [...KEY_FLASH_SALES, "detail", id],
		queryFn: () =>
			id ? flashSalesApi.get(id) : Promise.reject(new Error("ID requis")),
		enabled: Boolean(id),
	});
}

export function useCreateFlashSale() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (input: CreateFlashSaleInput) => flashSalesApi.create(input),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: KEY_FLASH_SALES });
		},
	});
}

export function useUpdateFlashSale() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({
			id,
			data,
		}: {
			id: string;
			data: UpdateFlashSaleInput;
		}) => flashSalesApi.update(id, data),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: KEY_FLASH_SALES });
		},
	});
}

export function useToggleFlashSaleStatus() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
			flashSalesApi.toggleStatus(id, isActive),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: KEY_FLASH_SALES });
		},
	});
}

export function useDeleteFlashSale() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => flashSalesApi.delete(id),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: KEY_FLASH_SALES });
		},
	});
}
